import React, {useRef, useState} from 'react';
import {Modal, ModalBody, ModalFooter, ModalHeader} from 'reactstrap';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {DownloadSimple, UploadSimple} from '../../icons';
import {Spinner} from '../../Spinner';
import {ClientEconomicsApi} from '../../../api/clientEconomicsApi';
import {PROFILE_FIELDS, formatProfileValue} from '../panel_v11/economyFormatters';
import {
    IMPORT_CASES,
    IMPORT_MAX_BYTES,
    IMPORT_WARNINGS,
    applicableCount,
    canApply,
    hasErrors,
    importRejection,
    importTemplateCsv,
    invalidColumnOf,
    isErrorCase,
    isExpiredResponse,
} from './state/importModel';

const TEMPLATE_FILE_NAME = 'economia-clientes.csv';

function downloadTemplate() {
    const blob = new Blob([importTemplateCsv()], {type: 'text/csv;charset=utf-8'});
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = TEMPLATE_FILE_NAME;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
}

/**
 * «Importar planilla» (contrato §3.5): una fila por cliente con los cinco datos. Primero la VISTA
 * PREVIA —que nombra el caso de cada fila: se crea, se actualiza, sin cambios, cliente no
 * encontrado, número inválido…— y recién después «Aplicar», que es todo o nada. Con filas con error
 * sólo se aplica si el usuario lo pide explícito («Aplicar igual las filas válidas»).
 *
 * Lo abren el Listado de clientes (vista Economía) y la pantalla de economía de un cliente: el
 * archivo es de la AGENCIA (varios clientes), no de uno.
 */
export function ImportEconomicsDialog({isOpen, onClose, onApplied}) {
    const {t} = useTranslation();
    const inputRef = useRef(null);
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [skipErrors, setSkipErrors] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const reset = () => {
        setFile(null);
        setPreview(null);
        setSkipErrors(false);
        setError(null);
        if (inputRef.current) inputRef.current.value = '';
    };

    const close = () => {
        if (busy) return;
        reset();
        onClose();
    };

    const pickFile = (event) => {
        const picked = event.target.files && event.target.files[0];
        setPreview(null);
        setSkipErrors(false);
        setError(null);
        if (picked && picked.size > IMPORT_MAX_BYTES) {
            setFile(null);
            setError(t('economics.import.error.too_large'));
            return;
        }
        setFile(picked || null);
    };

    // Un rechazo del archivo entero dice qué corregir (columnas que faltan, no es CSV, pesa de más);
    // el resto va al genérico. Nunca el texto del backend.
    const showRejection = (response) => {
        const {key, params} = importRejection(response);
        setError(t(key, params));
    };

    const runPreview = () => {
        if (!file || busy) return;
        setBusy(true);
        setError(null);
        ClientEconomicsApi.importPreview(file).then((response) => {
            setBusy(false);
            if (!response || response.status !== 'OK' || !response.data) {
                showRejection(response);
                return;
            }
            setPreview(response.data);
        }).catch(() => {
            setBusy(false);
            setError(t('economics.error.generic'));
        });
    };

    const runApply = () => {
        if (!preview || busy || !canApply(preview.summary, skipErrors)) return;
        setBusy(true);
        setError(null);
        ClientEconomicsApi.importApply(file, preview.token, skipErrors).then((response) => {
            setBusy(false);
            if (response && response.status === 'OK') {
                const summary = (response.data && response.data.summary) || preview.summary || {};
                // Tres números, tres claves con su propio plural (una sola clave tiene un solo
                // `count`, y decía «1 actualizados»: QA-eco-24).
                toast.success([
                    t('economics.import.done_created', {count: Number(summary.create) || 0}),
                    t('economics.import.done_updated', {count: Number(summary.update) || 0}),
                    t('economics.import.done_unchanged', {count: Number(summary.unchanged) || 0}),
                ].join(' · '));
                reset();
                if (onApplied) onApplied();
                onClose();
                return;
            }
            if (isExpiredResponse(response)) {
                setPreview(null);
                setError(t('economics.import.expired'));
                return;
            }
            showRejection(response);
        }).catch(() => {
            setBusy(false);
            setError(t('economics.error.generic'));
        });
    };

    // El nombre de un campo del diff: los del perfil y la moneda —que el backend suma en cada `CREATE`—
    // con su rótulo, como en las Versiones (R-ecofe-14). Uno que la pantalla no conoce, como viene.
    const fieldLabel = (field) => {
        if (PROFILE_FIELDS.includes(field)) return t(`client_panel.economy.input.${field}`);
        if (field === 'currency') return t('economics.form.currency');
        return field;
    };

    // Qué cambia en una fila que se crea o actualiza: el mismo renglón «campo: antes → después» que
    // las Versiones.
    const renderDiff = (row) => {
        const diff = row.diff || {};
        const fields = Object.keys(diff);
        if (isErrorCase(row.case) || !fields.length) return null;
        const format = (field, value) => {
            if (value === null || value === undefined || value === '') return '—';
            return PROFILE_FIELDS.includes(field) ? (formatProfileValue(field, value, null) || '—') : String(value);
        };
        return (
            <ul className="eco-import__diff">
                {fields.map((field) => {
                    const [before, after] = diff[field] || [];
                    return (
                        <li key={field}>
                            {t('economics.versions.diff', {
                                field: fieldLabel(field),
                                before: format(field, before),
                                after: format(field, after),
                                interpolation: {escapeValue: false},
                            })}
                        </li>
                    );
                })}
            </ul>
        );
    };

    // Avisos que NO bloquean la fila: hay una versión posterior que tapa a la nueva (el panel no va a
    // cambiar) o un porcentaje ≤ 1 que parece una fracción (¿0,55 era 55 %?). Un código desconocido
    // no se muestra.
    const renderWarnings = (row) => {
        const warnings = (Array.isArray(row.warnings) ? row.warnings : []).filter((code) => IMPORT_WARNINGS.has(code)
            // «Esta no va a quedar vigente» sólo tiene sentido si la fila crea una versión (QA-eco-24).
            && !(code === 'LATER_VERSION' && row.case === 'UNCHANGED'));
        if (!warnings.length) return null;
        return (
            <ul className="eco-import__warn">
                {warnings.map((code) => (
                    <li key={code} data-testid={`eco-import-warning-${row.row}-${code}`}>
                        {t(`economics.import.warning.${code}`)}
                    </li>
                ))}
            </ul>
        );
    };

    const caseText = (row) => {
        if (!IMPORT_CASES.includes(row.case)) return '—';
        if (row.case === 'INVALID_NUMBER') {
            return t('economics.import.case.INVALID_NUMBER', {column: invalidColumnOf(row),
                interpolation: {escapeValue: false}});
        }
        return t(`economics.import.case.${row.case}`);
    };

    const renderPreview = () => {
        if (!preview) return null;
        const rows = Array.isArray(preview.rows) ? preview.rows : [];
        const summary = preview.summary || {};
        return (
            <div className="eco-import__preview" data-testid="eco-import-preview">
                <table className="eco-import__table">
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.row} className={isErrorCase(row.case) ? '-error' : ''}
                                data-testid={`eco-import-row-${row.row}`} data-case={row.case}>
                                <td className="eco-import__n">{row.row}</td>
                                <td className="eco-import__client">{(row.client && row.client.name) || '—'}</td>
                                <td>
                                    <span className={`eco-case -${String(row.case || '').toLowerCase()}`}>
                                        {caseText(row)}
                                    </span>
                                    {renderDiff(row)}
                                    {renderWarnings(row)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {hasErrors(summary) && (
                    <label className="eco-import__skip">
                        <input type="checkbox" checked={skipErrors}
                               onChange={(event) => setSkipErrors(event.target.checked)}
                               data-testid="eco-import-skip"/>
                        {t('economics.import.skip_errors')}
                    </label>
                )}
            </div>
        );
    };

    const count = preview ? applicableCount(preview.summary) : 0;

    return (
        <Modal isOpen={isOpen} toggle={close} className="eco-import-modal" size="lg" labelledBy="eco-import-title">
            <ModalHeader toggle={close} id="eco-import-title">{t('economics.import.title')}</ModalHeader>
            <ModalBody>
                <div className="eco-import" data-testid="eco-import">
                    <p className="eco-import__body">{t('economics.import.body')}</p>
                    <div className="eco-import__pick">
                        <button type="button" className="eco-link" onClick={downloadTemplate}>
                            <DownloadSimple size={14} weight="bold"/>
                            {t('economics.import.template')}
                        </button>
                        <input ref={inputRef} type="file" accept=".csv,text/csv" onChange={pickFile}
                               aria-label={t('economics.import.title')} data-testid="eco-import-file"/>
                    </div>
                    {busy && <div className="box-loader"><Spinner color="positive small"/></div>}
                    {error && <p className="eco-import__error" role="alert">{error}</p>}
                    {renderPreview()}
                </div>
            </ModalBody>
            <ModalFooter>
                <button type="button" className="btn -negative" onClick={close} disabled={busy}>
                    {t('economics.form.cancel')}
                </button>
                {!preview ? (
                    <button type="button" className="btn -alt" onClick={runPreview} disabled={!file || busy}
                            data-testid="eco-import-preview-btn">
                        <UploadSimple size={14} weight="bold"/>
                        {t('economics.import.preview')}
                    </button>
                ) : (
                    <button type="button" className="btn -alt" onClick={runApply}
                            disabled={busy || !canApply(preview.summary, skipErrors)}
                            data-testid="eco-import-apply">
                        {t('economics.import.apply', {count})}
                    </button>
                )}
            </ModalFooter>
        </Modal>
    );
}

export default ImportEconomicsDialog;
